'use strict';

const d3 = require('d3');
const _ = require('underscore');
const Backbone = require('backbone');
const TextpathView = require('common/ui/view/svg/text/path');
const SVGUtil = require('common/ui/view/svg/util');
const i18n = require('i18next');

module.exports = Backbone.View.extend({
    menus: [],
    /**
     *  メニューを作成、表示するためのクラスです。
     *  メニューのアイテムに表示するアイコンは、
     *  balus/views/diagram/menu/icon を利用して下さい。
     *
     *  @example
     *  const MyMenu = HolyMenu.extend({
             *      menus: [
             *      {
             *          name: 'menu1',  // メニューの名前、ユニーク
             *          icon: MenuIcon.fontAwesomeIcon(
             *              'fa fa-edit',
             *              {color: 'black'}
             *          ),
             *          click: function(menu) {
             *              //処理...
             *              menu.hide();
             *          }
             *      },
             *      {
             *          ...
             *      }]
             *  })
     *
     *  const myMenu = new MyMenu({
             *      svg: canvas.canvas,
             *      r: 80
             *      itemRadius: 20
             *  });
     *
     *  @constructs
     */
    initialize: function(params) {
        this.r = params.r ? params.r : 80;
        this.itemR = params.itemRadius ? params.itemRadius : 20;

        this.pos = null;
        this.scale = 1;
        this.scaleFactor = 1;
    },

    /**
     * メニューの見た目のサイズをスケールが変わっても同じままにするためのメソッド
     * @param scale
     */
    unzoom: function(scale) {
        if(!this.pos) {
            return;
        }

        this.scale = scale;
        const pos = this.pos;
        pos.scale = 1 / scale * this.scaleFactor;

        this.svg
            .attr('transform', SVGUtil.transform(pos))
    },

    /**
     * メニューを表示する
     *
     * @param svg {Object} - d3 svg object
     * @param pos {Object}
     * @param pos.x {Number} - x 座標
     * @param pos.y {Number} - y 座標
     * @param scale {Number} - スケール
     * @param opts {Object}
     * @param opts.circle {Boolean} - アイテムが円を作るかどうか。 false ならば円弧になる
     * @param opts.startAngle {Number} - 360 度指定での円弧の開始確度
     * @param opts.endAngle {Number} - 360 度指定での円弧の終了確度
     * @param opts.clockwise {Boolean} - メニューを時計回りで配置するかどうか
     */
    render: function(svg, pos, scale, opts) {
        if(this.menus.length > 2) {
            const t = Math.PI / this.menus.length;
            const minR = (this.itemR + 12) / Math.sin(t);
            if(minR > this.r) {
                this.r = minR;
            }
        }

        this.scaleFactor = 1;
        this.opts = opts;
        opts = _.extend({
            circle: true,
            startAngle: 90,
            endAngle: 450,
            clockwise: true
        }, opts);

        this.size = {
            r: this.itemR,
            width: this.itemR * 2,
            height: this.itemR * 2
        };
        this.pos = pos;
        this.scale = scale;
        pos.scale = 1 / scale * this.scaleFactor;

        if(svg) {
            if(this.svg) {
                this.svg.remove();
            }
            this.svg = svg.append('g');
        }

        this.svg
            .attr('transform', SVGUtil.transform(pos))
            .classed('menu', true)
            .style('opacity', 1)
            .on('mousedown', this.stopEvent)
            .on('touchstart', this.stopEvent);

        // メニューのアイテムをつなぐリング
        this.svg
            .append('circle')
            .classed('menu-ring theme-menu-ring', true)
            .attr('r', this.r * 0.8)
            .style('opacity', 0.2);

        const self = this;
        const menus = this.svg.selectAll('.menu-item')
            .data(this.menus, function(d){
                return d.name;
            })
            .enter()
            .append('g')
            .on('click', function(d) {
                d.click(self, d);
            })
            .classed('menu-item theme-menu-item', true)
            .style('opacity', 0.5)
            .attr('transform', 'translate(0,0)')

        menus
            .append('circle')
            .classed('menu-item-bg theme-menu-item-bg', true)
            .attr('r', this.size.r + 4)
            .style('opacity', 0.9);

        const size = this.size;
        const textR = this.size.r + 4.5;
        const fromX = 0;
        const fromY = textR;
        const toX1 = 0;
        const toY1 = -textR;
        const toX2 = 0;
        const toY2 = textR;
        const rx = textR;
        const ry = textR;
        const clockwise = 1;
        const path = `M ${fromX},${fromY} A ${rx},${ry} 0 0,${clockwise} ${toX1},${toY1} A ${rx},${ry} 0 0,${clockwise} ${toX2},${toY2}`;
        menus
            .select(function(d){
                let text = '';
                if(d.text) {
                    text = i18n.t(d.text);
                }
                const view = new TextpathView({
                    id: this.id + ':' + d.name,
                    path: path,
                    offset: '50%'
                });
                view.render();
                view.text(text);
                return this.appendChild(view.el);
            })
            .classed('menu-item-label theme-menu-item-label', true);

        menus
            .attr('t', function(d) {
                d.icon(d3.select(this), size);
            });

        const n = opts.circle ? this.menus.length : this.menus.length - 1;
        let startAngle = opts.startAngle;
        let endAngle = opts.circle ? startAngle + 360 : opts.endAngle;

        // 逆回りなら start と end を入れ替える
        if(!opts.clockwise) {
            const tmp = startAngle;
            startAngle = endAngle;
            endAngle = tmp;
        }

        startAngle = startAngle / 360 * Math.PI * 2;
        endAngle = endAngle / 360 * Math.PI * 2;

        for(let i = 0; i<this.menus.length; i++) {
            this.menus[i].angle = startAngle + (endAngle - startAngle) / n * i;
        }
        const r = this.r;
        const ring = this.svg.select('.menu-ring');
        menus
            .attr('transform', function(d) {
                const x = - r * 0.4 * Math.cos(d.angle);
                const y = - r * 0.4 * Math.sin(d.angle);
                d.x = x;
                d.y = y;

                return 'translate(' + x + ',' + y + ')';
            });
        menus
            .transition()
            .duration(300)
            .ease(d3.easeQuad)
            .attr('transform', function(d) {
                const x = - r * Math.cos(d.angle);
                const y = - r * Math.sin(d.angle);
                d.x = x;
                d.y = y;

                return 'translate(' + x + ',' + y + ')';
            })
            .style('opacity', 1.0);

        const maxR = this.r + this.itemR * 2;
        ring
            .transition()
            .duration(1500)
            .ease(d3.easeElastic)
            .attr('r', maxR)
            .style('opacity', 0.8);
    },

    stopEvent: function() {
        d3.event.stopPropagation();
    },

    hide: function() {
        if(!this.svg) {
            return;
        }
        this.svg
            .style('opacity', 0)
            .style('pointer-events', 'none');
    },

    shrink: function() {
        this.scaleFactor = 0.8;
        const pos = this.pos;
        pos.scale = 1 / this.scale * this.scaleFactor;
        this.svg
            .transition()
            .duration(200)
            .ease('sin')
            .attr('transform', SVGUtil.transform(pos));
    },

    remove: function() {
        this.svg.remove();
    }
});



// WEBPACK FOOTER //
// ./common/ui/view/svg/menu/holy.js